import React, { useEffect, useMemo, useState, useCallback } from "react";
import { VsiBubbleFavs } from "../styled/App.styled";
import { useAuthWithRoute, useDataWithRoute } from "../utils/hooks";
import { Spinner } from "reactstrap";
import { forecastWithRateV2, formatStockChartConfig } from "../utils/Helpers";
import Stockchart from "../components/Stockchart";
import dayjs from "dayjs";
import { useVsiAppContext } from "../stores/VsiApp";

const allTypes = [
  {
    name: "cost",
    label: "Cost",
    chartDefs: [
      { name: "Cost per Month", field: "total_cost" },
      { name: "On Prem Cost", field: "on_prem_cost" },
      { name: "Budget", field: "budget" }
    ],
    chartConfig: {
      yAxis: {
        title: {
          text: "$ USD"
        }
      }
    }
  },
  {
    name: "vcpu",
    label: "vCPU",
    chartDefs: [
      { name: "Virtual CPU Used", field: "cpu_used" }
    ],
    chartConfig: {
      yAxis: {
        title: {
          text: "CPUs"
        }
      }
    }
  },
  {
    name: "disk",
    label: "Disk",
    chartDefs: [
      { name: "Disk Used (GiB)", field: "disk_used" }
    ],
    chartConfig: {
      yAxis: {
        title: {
          text: "GiB"
        }
      }
    }
  },
  {
    name: "memory",
    label: "Memory",
    chartDefs: [
      { name: "Memory Used (GiB)", field: "memory_used" }
    ],
    chartConfig: {
      yAxis: {
        title: {
          text: "GiB"
        }
      }
    }
  },
  {
    name: "vmbuild",
    label: "VMs",
    chartDefs: [
      { name: "# of VMs", field: "total_vms" }
    ],
    chartConfig: {
      yAxis: {
        title: {
          text: "VMs"
        }
      }
    }
  }
];

export default function CloudResourceModeling({ params }) {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("CLOUD");


  const { chosenResourceGroup, chosenChartType } = useMemo(() => {
    let chosenResourceGroup;
    let chosenChartType;
    for (let i = 0; i < params.length; i++) {
      if (params?.[i]?.param === "resource_group") {
        chosenResourceGroup = params[i].value;
      } else if (params?.[i]?.param === "chart_type") {
        chosenChartType = params[i].value;
      }
    }
    return {
      chosenResourceGroup,
      chosenChartType
    };
  }, [params]);

  const resourceGroupRoute = useMemo(() => `/api/v6/report/cloud_all_resource_groups_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [allResourceGroups, { isLoading: isAllResourceGroupsLoading }] = useDataWithRoute(resourceGroupRoute);

  const allGroups = useMemo(() => {
    return allResourceGroups?.recordset?.map?.(group => ({
      value: group.resource_group,
      label: group.resource_group
    })) ?? [];
  }, [allResourceGroups.recordset]);

  const activeGroup = useMemo(() => {
    return chosenResourceGroup && allGroups.length ? allGroups.find(group => group.value === decodeURI(chosenResourceGroup)) : allGroups[0];
  }, [allGroups, chosenResourceGroup]);

  const savedPlanRoute = useMemo(() => `/api/v6/admin/resource_group_capacity_model/${clientId}/${encodeURI(activeGroup?.value)}`, [clientId, activeGroup]);
  const [savedPlanData] = useAuthWithRoute(savedPlanRoute);

  const historyRoute = useMemo(() => {
    let route = "";
    if (activeGroup?.value) {
      route = `/api/v6/report/cloud_history_modeling_by_resource_group_proc?client_id=${clientId}&period_id=${periodId}&resource_group=${encodeURI(activeGroup?.value)}`;
    }

    return route;
  }, [clientId, periodId, activeGroup]);
  const [resourceGroupDetails, { isLoading: isResourceGroupDetailsLoading }] = useDataWithRoute(historyRoute);

  const [activeCapacityPlan, setActiveCapacityPlan] = useState({
    isBudgetForecasted: true
  }); // would need to do a get from mongo


  const handleCapacityPlan = useCallback(data => {
    const capacity = {
      cpu: 0,
      mem: 0,
      disk: 0,
      vm: 0,
      cost: 0
    };

    const workload = { ...capacity };

    let budgetDiff = 0;
    return (data || []).map(datum => {
      const newDatum = { ...datum };
      const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");
      let budget = datum.budget;
      if (typeof activeCapacityPlan?.[dateToCompare]?.customWorkload === "object") {
        // do something
        const { customWorkload } = activeCapacityPlan[dateToCompare];

        workload.cpu = workload.cpu + customWorkload.cpu;
        workload.mem = workload.mem + customWorkload.mem;
        workload.disk = workload.disk + customWorkload.disk;
        workload.vm = workload.vm + customWorkload.vm;
        workload.cost = workload.cost + (customWorkload.cost ?? 0);
      }

      if (activeCapacityPlan?.[dateToCompare]?.budget) {
        budget = parseFloat(activeCapacityPlan?.[dateToCompare]?.budget);
        budgetDiff = budget - datum.budget;
      }

      //workload
      newDatum.cpu_used = newDatum.cpu_used + workload.cpu;
      newDatum.memory_used = newDatum.memory_used + workload.mem;
      newDatum.disk_used = newDatum.disk_used + workload.disk;
      newDatum.total_vms = newDatum.total_vms + workload.vm;
      newDatum.total_cost = newDatum.total_cost + workload.cost;
      newDatum.budget = newDatum.budget + budgetDiff;
      return newDatum;
    });
  }, [activeCapacityPlan]);

  const handleBudgetPlan = useCallback(data => {
    return (data || []).map(datum => {
      const newDatum = { ...datum };
      const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");
      if (activeCapacityPlan?.[dateToCompare]?.budget) {
        newDatum.budget = parseFloat(activeCapacityPlan?.[dateToCompare]?.budget);
      }
      return newDatum;
    });
  }, [activeCapacityPlan]);

  const { chartTrendData, chartConfig } = useMemo(() => {
    const data = resourceGroupDetails?.recordsets?.[0] ?? [];
    let allFieldsForForecast = [];
    allTypes.forEach(type => {
      type.chartDefs.forEach(def => {
        if (def.field) allFieldsForForecast.push(def.field);
      });
    });

    const chartTypeFound = allTypes.find(type => type.name === chosenChartType) || allTypes[0];
    const budgetedData = handleBudgetPlan(data);
    if (!activeCapacityPlan?.isBudgetForecasted) {
      allFieldsForForecast = allFieldsForForecast.filter(field => field !== "budget");
    }

    const newData = [...budgetedData, ...forecastWithRateV2(budgetedData || [], allFieldsForForecast, 3)];
    const withPlanData = handleCapacityPlan(newData);

    const avgOnPremCost = Math.round((withPlanData.reduce((a, b) => a + b.total_cost, 0) / withPlanData.length) * 1.5);
    const formattedData = formatStockChartConfig(withPlanData.map(plan => ({
      ...plan,
      on_prem_cost: avgOnPremCost
    })), chartTypeFound?.chartDefs || []);

    return {
      chartTrendData: formattedData.series.map(series => ({
        ...series,
        zoneAxis: "x",
        zones: [{
          value: Date.parse(data?.[data.length - 1]?.collection_date?.split?.("T")?.[0])
        }, {
          dashStyle: "Dash"
        }]
      })),
      chartConfig: chartTypeFound.chartConfig
    };
  }, [resourceGroupDetails.recordsets, activeCapacityPlan, chosenChartType, handleBudgetPlan, handleCapacityPlan]);

  const formatSavedData = (data) => {
    const savedPlan = data?.[0];
    const activePlan = {
      isBudgetForecasted: true
    };
    const activeBudgets = {};

    if (savedPlan?._id) {
      (savedPlan?.adjustments ?? []).forEach(adjustment => {
        if (!activePlan[adjustment.actualDate]) {
          activePlan[adjustment.actualDate] = {};
        }
        activePlan[adjustment.actualDate] = { ...adjustment };
        if (adjustment.budget) {
          activeBudgets[adjustment.actualDate] = adjustment.budget;
        }
      });
      activePlan.isBudgetForecasted = savedPlan.isBudgetForecasted;
    }

    setActiveCapacityPlan(activePlan);
  };

  useEffect(() => {
    formatSavedData(savedPlanData);
  }, [savedPlanData]);

  return (isAllResourceGroupsLoading && isResourceGroupDetailsLoading) ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubbleFavs>
      <Stockchart series={chartTrendData} config={chartConfig} navDisabled={true} disableLegend={true}
                  height={400} />
    </VsiBubbleFavs>
  );
}
